/**
 * 파일 경로에서 확장자를 추출합니다.
 *
 * @param filePath - 파일 경로 또는 파일명
 * @returns 확장자 (대문자), 확장자가 없으면 null
 *
 * @example
 * getFileExtension('document.pdf') // 'PDF'
 * getFileExtension('/path/to/file.json') // 'JSON'
 * getFileExtension('/path.with.dots/file.tar.gz') // 'GZ'
 * getFileExtension('README') // null
 * getFileExtension('/folder.name/file') // null
 */
export const getFileExtension = (filePath: string): string | null => {
  const fileName = filePath.split('/').pop() || '';

  const parts = fileName.split('.');

  if (parts.length <= 1 || (parts.length === 2 && parts[0] === '')) {
    return null;
  }

  return parts.pop()?.toUpperCase() || null;
};

/**
 * 파일 경로에서 확장자를 추출하며, 없으면 기본값을 반환합니다.
 *
 * @param filePath - 파일 경로 또는 파일명
 * @param fallback - 확장자가 없을 때 반환할 기본값 (기본값: 'N/A')
 * @returns 확장자 (대문자) 또는 기본값
 *
 * @example
 * getFileExtensionSafe('document.pdf') // 'PDF'
 * getFileExtensionSafe('README') // 'N/A'
 * getFileExtensionSafe('README', 'UNKNOWN') // 'UNKNOWN'
 */
export const getFileExtensionSafe = (filePath: string, fallback: string = 'N/A'): string => {
  return getFileExtension(filePath) || fallback;
};

/**
 * 파일 경로에서 파일명(확장자 포함)을 추출합니다.
 *
 * @param filePath - 파일 경로
 * @returns 파일명
 *
 * @example
 * getFileName('/path/to/document.pdf') // 'document.pdf'
 * getFileName('document.pdf') // 'document.pdf'
 * getFileName('/path/to/folder/') // ''
 */
export const getFileName = (filePath: string): string => {
  return filePath.split('/').pop() || '';
};
